import { useQuery } from '@tanstack/react-query';

import { catalogCompanySourceMapApi } from 'api/inventory/catalogCompanySourceMap';

export const COMPANY_SOURCE_OPTIONS_BY_CATALOG_ITEM_QUERY_KEY = 'catalogCompanySourceOptionsByCatalogItem';

/**
 * Fetches company source options scoped to an external SKU or packaging id.
 *
 * @param {Object} [options]
 * @param {string} [options.externalSku]
 * @param {number | string} [options.packagingId]
 * @param {boolean} [options.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<{text:string,value:number|string}>, Error>}
 */
export const useCompanySourceOptionsByCatalogItemQuery = ({ enabled = true, externalSku, packagingId } = {}) => {
    const hasExternalSku = externalSku !== undefined && externalSku !== null && externalSku !== '';
    const hasPackagingId = packagingId !== undefined && packagingId !== null && packagingId !== '';

    return useQuery({
        queryKey: [COMPANY_SOURCE_OPTIONS_BY_CATALOG_ITEM_QUERY_KEY, { externalSku, packagingId }],
        enabled: enabled && (hasExternalSku || hasPackagingId),
        queryFn: async () => {
            const response = hasExternalSku
                ? await catalogCompanySourceMapApi.getCompanySourceOptionsByExternalSku(externalSku)
                : await catalogCompanySourceMapApi.getCompanySourceOptionsByPackagingId(packagingId);

            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch company sources');
            }

            const companySources = response?.data?.companySources ?? [];
            return companySources.map((item) => ({
                text: item.displayName,
                value: item.id,
            }));
        },
        staleTime: 15 * 60_000,
        gcTime: 30 * 60_000,
    });
};
